import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axiosInstance from "../../utils/axios";

export interface AppNotification {
  id: number;
  type: string;
  title: string;
  body: string | null;
  linkPath: string | null;
  readAt: string | null;
  createdAt: string;
}

interface NotificationsResponse {
  items: AppNotification[];
  total: number;
  unreadCount: number;
  limit: number;
  offset: number;
}

interface NotificationsState {
  items: AppNotification[];
  unreadCount: number;
  total: number;
  status: "idle" | "loading" | "failed";
}

const initialState: NotificationsState = {
  items: [],
  unreadCount: 0,
  total: 0,
  status: "idle",
};

export const fetchNotifications = createAsyncThunk<NotificationsResponse>(
  "notifications/fetch",
  async () => {
    const res = await axiosInstance.get<NotificationsResponse>(
      "/notifications",
      { params: { limit: 20 } }
    );
    return res.data;
  }
);

export const markNotificationRead = createAsyncThunk<AppNotification, number>(
  "notifications/markRead",
  async (id) => {
    const res = await axiosInstance.put<AppNotification>(
      `/notifications/${id}/read`
    );
    return res.data;
  }
);

export const markAllNotificationsRead = createAsyncThunk(
  "notifications/markAllRead",
  async () => {
    await axiosInstance.put("/notifications/read-all");
  }
);

const slice = createSlice({
  name: "notifications",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(
        fetchNotifications.fulfilled,
        (state, action: PayloadAction<NotificationsResponse>) => {
          state.items = action.payload.items;
          state.unreadCount = action.payload.unreadCount;
          state.total = action.payload.total;
          state.status = "idle";
        }
      )
      .addCase(fetchNotifications.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(markNotificationRead.fulfilled, (state, action) => {
        const item = state.items.find((n) => n.id === action.payload.id);
        if (item && !item.readAt) {
          item.readAt = action.payload.readAt;
          state.unreadCount = Math.max(0, state.unreadCount - 1);
        }
      })
      .addCase(markAllNotificationsRead.fulfilled, (state) => {
        const now = new Date().toISOString();
        state.items.forEach((n) => {
          if (!n.readAt) n.readAt = now;
        });
        state.unreadCount = 0;
      });
  },
});

export default slice.reducer;
